<script setup lang="ts">
import type { EyeParameters } from '@/models/EyeParameters';
import { computed, type PropType } from 'vue';

const props = defineProps({
    eyeParameters: {
        type: Object as PropType<EyeParameters>,
        required: true
    },
    // Recurso em teste — SIA só aparece atrás da trava (?beta=torica).
    showSia: {
        type: Boolean,
        required: false,
        default: false
    },
    // Falso na Afacia: essa fórmula não usa ACD/LT/WTW (olho sem
    // cristalino natural, não dá pra medir com confiança), o payload manda
    // 0 neles, e mostrar "0.00mm" no resultado só confundiria.
    showAcdLtWtw: {
        type: Boolean,
        required: false,
        default: true
    }
})

const cylValue = computed(() => {
    return props.eyeParameters.k.k1.k - props.eyeParameters.k.k2.k;
});

const cylColorClass = computed(() => {
    const val = Math.abs(cylValue.value);
    if (val <= 0.75) {
        return 'cyl-green';
    } else if (val <= 1.50) {
        return 'cyl-yellow';
    } else {
        return 'cyl-red';
    }
});
</script>

<template>
    <div class="container">
        <div class="parameters">
            <p class="parameters-row">
                <span class="parameters-row__parameter">AL:</span>
                <span class="parameters-row__value">{{ eyeParameters.al.toFixed(2) }} <span style="font-size: 0.5em">mm</span></span>
            </p>

            <p class="parameters-row" v-if="showAcdLtWtw">
                <span class="parameters-row__parameter">ACD:</span>
                <span class="parameters-row__value">{{ eyeParameters.acd.toFixed(2) }} <span style="font-size: 0.5em">mm</span></span>
            </p>

            <p class="parameters-row" v-if="showAcdLtWtw">
                <span class="parameters-row__parameter">LT:</span>
                <span class="parameters-row__value">{{ eyeParameters.lt.toFixed(2) }} <span style="font-size: 0.5em">mm</span></span>
            </p>

            <p class="parameters-row" v-if="showAcdLtWtw">
                <span class="parameters-row__parameter">WTW:</span>
                <span class="parameters-row__value">{{ eyeParameters.wtw.toFixed(2) }} <span style="font-size: 0.5em">mm</span></span>
            </p>
        </div>

        <div class="parameters">
            <p class="parameters-row">
                <span class="parameters-row__parameter">K1 (Flat):</span>
                <span class="parameters-row__value">{{ eyeParameters.k.k1.k.toFixed(2) }} D</span>
            </p>

            <p class="parameters-row">
                <span class="parameters-row__parameter">K1 Axis:</span>
                <span class="parameters-row__value">{{ eyeParameters.k.k1.axis }} º</span>
            </p>

            <p class="parameters-row">
                <span class="parameters-row__parameter">K2 (Steep):</span>
                <span class="parameters-row__value">{{ eyeParameters.k.k2.k.toFixed(2) }} D</span>
            </p>

            <p class="parameters-row">
                <span class="parameters-row__parameter">K2 Axis:</span>
                <span class="parameters-row__value">{{ eyeParameters.k.k2.axis }} º</span>
            </p>

            <p class="parameters-row">
              <span class="parameters-row__parameter">Cyl:</span>
              <span class="parameters-row__value" :class="cylColorClass">{{ cylValue.toFixed(2) }}</span>
            </p>

            <p class="parameters-row">
                <span class="parameters-row__parameter">Incision:</span>
                <span class="parameters-row__value">{{ eyeParameters.k.incision }} º</span>
            </p>

            <p class="parameters-row" v-if="showSia">
                <span class="parameters-row__parameter">SIA:</span>
                <span class="parameters-row__value">{{ eyeParameters.k.sia.toFixed(2) }} D</span>
            </p>
        </div>
    </div>
</template>

<style scoped lang="sass">

.container
    display: flex
    flex-direction: column
    gap: 3em

.parameters
    display: flex
    gap: 3em

    @media screen and (max-width: 545px) 
        flex-direction: column
        gap: 1em

    &-row
        display: block

        @media screen and (max-width: 545px)
            display: flex
            align-items: baseline
            gap: 1em

    &-row__parameter
        font-size: 1.4em
        display: block

        @media screen and (max-width: 545px)
            width: 50%
            text-align: right

        @media print
            font-size: 2em
    &-row__value
        font-size: 2em
        display: block

        @media screen and (max-width: 545px)
            width: 50%

        @media print
          font-size: 2.7em

        &.cyl-green
          color: #15803d
          background-color: #f0fdf4
          padding: 2px 8px
          border-radius: 4px
          display: inline-block
          -webkit-print-color-adjust: exact
          print-color-adjust: exact

        &.cyl-yellow
          color: #a16207
          background-color: #fefce8
          border: 1.5px solid #eab308
          padding: 0px 6px
          border-radius: 4px
          display: inline-block
          -webkit-print-color-adjust: exact
          print-color-adjust: exact

        &.cyl-red
          color: #b91c1c
          background-color: #fef2f2
          padding: 2px 8px
          border-radius: 4px
          display: inline-block
          -webkit-print-color-adjust: exact
          print-color-adjust: exact
</style>